export type TimeZoneDateParts = {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
};

export type DateRange = {
    start: Date;
    end: Date;
};

/**
 * Formats a date string or object into a compact relative time string.
 * Examples: "1h", "2d", "1w", "1m", "1y"
 * @param date The date to format (string or Date object)
 * @returns Compact relative time string
 */
export function formatCompactTimeAgo(date: string | Date | undefined | null): string {
    if (!date) return "";

    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.round((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return "now";
    }

    const minutes = Math.round(diffInSeconds / 60);
    if (minutes < 60) {
        return `${minutes}m`;
    }

    const hours = Math.round(minutes / 60);
    if (hours < 24) {
        return `${hours}h`;
    }

    const days = Math.round(hours / 24);
    if (days < 7) {
        return `${days}d`;
    }

    const weeks = Math.round(days / 7);
    if (weeks < 4) {
        return `${weeks}w`;
    }

    const months = Math.round(days / 30);
    if (months < 12) {
        return `${months}mo`;
    }

    const years = Math.round(days / 365);
    return `${years}y`;
}

/**
 * Formats a future date into a compact relative time string.
 * Examples: "30d", "1h", "now"
 * @param date The future date
 * @returns Compact relative time string
 */
export function formatCompactTimeRemaining(date: string | Date | undefined | null): string {
    if (!date) return "";
    const now = new Date();
    const future = new Date(date);
    const diffInSeconds = Math.round((future.getTime() - now.getTime()) / 1000);

    if (diffInSeconds <= 0) {
        return "Expired";
    }

    if (diffInSeconds < 60) {
        return "now";
    }

    const minutes = Math.round(diffInSeconds / 60);
    if (minutes < 60) {
        return `${minutes}m`;
    }

    const hours = Math.round(minutes / 60);
    if (hours < 24) {
        return `${hours}h`;
    }

    const days = Math.round(hours / 24);
    if (days < 7) {
        return `${days}d`;
    }

    const weeks = Math.round(days / 7);
    if (weeks < 4) {
        return `${weeks}w`;
    }
    const months = Math.round(days / 30);
    if (months < 12) {
        return `${months} mo`;
    }

    const years = Math.round(days / 365);
    return `${years}y`;
}

/**
 * Adds days to a date.
 * @param date The starting date
 * @param days The number of days to add
 * @returns New Date object
 */
export function addDays(date: string | Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

/**
 * Formats a Date object to a UTC date-time string in "YYYY-MM-DD HH:mm:ss" format.
 * E.g., "2026-05-21T17:44:44.123Z" -> "2026-05-21 17:44:44"
 * @param date The Date object to format
 * @returns Formatted date-time string
 */
export function formatUtcDateTimeSeconds(date: Date): string {
    return date.toISOString().slice(0, 19).replace("T", " ");
}

/**
 * Formats a date-like value into DD/MM/YYYY for user-facing displays.
 * Returns null when the value is missing or cannot be parsed as a date.
 */
export function formatDateDdMmYyyy(
    value: string | Date | undefined | null
): string | null {
    if (!value) {
        return null;
    }

    if (typeof value === "string") {
        const datePartsMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (datePartsMatch) {
            return `${datePartsMatch[3]}/${datePartsMatch[2]}/${datePartsMatch[1]}`;
        }
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
}

/**
 * Resolves and validates a time zone name, returning the time zone string if valid,
 * or null if invalid or undefined.
 * @param timeZone The name of the time zone to resolve
 * @returns The resolved time zone name, or null if invalid
 */
export function resolveTimeZone(timeZone?: string): string | null {
    if (!timeZone) {
        return null;
    }

    try {
        new Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
        return timeZone;
    } catch {
        return null;
    }
}

/**
 * Converts a Date object into individual date and time parts in a specific time zone.
 * @param date The Date object to extract parts from
 * @param timeZone The target time zone name
 * @returns Object containing year, month, day, hour, minute, and second as numbers
 */
export function getTimeZoneDateParts(date: Date, timeZone: string): TimeZoneDateParts {
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    });

    const parts = formatter.formatToParts(date);
    const getPart = (type: Intl.DateTimeFormatPartTypes): number =>
        Number(parts.find((part) => part.type === type)?.value || 0);

    return {
        year: getPart("year"),
        month: getPart("month"),
        day: getPart("day"),
        hour: getPart("hour"),
        minute: getPart("minute"),
        second: getPart("second"),
    };
}

/**
 * Converts a localized date-time (specified by parts) in a specific time zone to a UTC Date object.
 * Correctly accounts for Daylight Saving Time (DST) changes by iteratively adjusting offsets.
 * @param parts Date and time components (year, month, day, and optional hour, minute, second)
 * @param timeZone The time zone in which the parts are defined
 * @returns The corresponding UTC Date object
 */
export function zonedDateTimeToUtc(
    parts: Pick<TimeZoneDateParts, "year" | "month" | "day"> & {
        hour?: number;
        minute?: number;
        second?: number;
    },
    timeZone: string
): Date {
    const targetUtcMs = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour ?? 0,
        parts.minute ?? 0,
        parts.second ?? 0
    );

    let guess = new Date(targetUtcMs);

    for (let index = 0; index < 2; index += 1) {
        const zonedParts = getTimeZoneDateParts(guess, timeZone);
        const zonedUtcMs = Date.UTC(
            zonedParts.year,
            zonedParts.month - 1,
            zonedParts.day,
            zonedParts.hour,
            zonedParts.minute,
            zonedParts.second
        );

        guess = new Date(guess.getTime() - (zonedUtcMs - targetUtcMs));
    }

    return guess;
}

/**
 * Computes the calendar date components of the previous day in UTC.
 * @param parts The base year, month, and day components
 * @returns The year, month, and day components of the previous day
 */
export function getPreviousDateParts(
    parts: Pick<TimeZoneDateParts, "year" | "month" | "day">
): Pick<TimeZoneDateParts, "year" | "month" | "day"> {
    const previous = new Date(Date.UTC(parts.year, parts.month - 1, parts.day) - 24 * 60 * 60 * 1000);

    return {
        year: previous.getUTCFullYear(),
        month: previous.getUTCMonth() + 1,
        day: previous.getUTCDate(),
    };
}

/**
 * Computes the exact UTC Date range representing the full previous local day for a given time zone.
 * @param timeZone The target time zone name
 * @param now Optional base Date (defaults to the current system time)
 * @returns DateRange object representing the start and end of the previous day
 */
export function getPreviousLocalDayRange(timeZone: string, now: Date = new Date()): DateRange {
    const safeTimeZone = resolveTimeZone(timeZone);
    if (!safeTimeZone) {
        throw new Error(`Invalid timezone: ${timeZone}`);
    }

    const todayParts = getTimeZoneDateParts(now, safeTimeZone);
    const previousParts = getPreviousDateParts(todayParts);

    return {
        start: zonedDateTimeToUtc(previousParts, safeTimeZone),
        end: zonedDateTimeToUtc(
            { year: todayParts.year, month: todayParts.month, day: todayParts.day },
            safeTimeZone
        ),
    };
}

/**
 * Whether the given date (defaults to now, local time) falls on a Monday.
 * Used to word payout-finalization copy: "on Monday" vs "next Monday".
 */
export function isTodayMonday(now: Date = new Date()): boolean {
    return now.getDay() === 1;
}
